import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bell, Menu, X, User, LogOut, Package, MessageSquare, Plus, Search, Settings, Users, HeadphonesIcon, Wrench, Video, Star, ShoppingBag, GraduationCap } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import NotificationBell from './NotificationBell';

const Header = () => {
  const { user, logout, isSubscribed, getTrialDaysLeft, canSell } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/browse?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const trialDays = getTrialDaysLeft();
  const isSeller = user?.is_seller;
  const isVipCode = user?.promo_code_used === 'CMFFL26EF';
  const isFreeForLife = user?.subscription_status === 'free_for_life' || isVipCode;
  const onTrial = user?.subscription_status === 'trial' && !isFreeForLife;
  const hasPromoCode = user?.promo_code_used && !isVipCode;

  return (
    <header className="glass-header sticky top-0 z-50">
      {/* VIP Banner - Free for Life (CMFFL26EF) */}
      {user && isFreeForLife && (
        <div className="bg-gradient-to-r from-purple-700 to-purple-600 text-white text-center py-1.5 text-sm">
          <span className="font-medium">🌟 VIP Member</span> - Free for life! No subscription needed.
        </div>
      )}
      
      {/* Promo Code Active Banner - For LAUNCH26, CM2026, etc. */}
      {user && isSeller && onTrial && hasPromoCode && !isFreeForLife && trialDays > 0 && (
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-center py-1.5 text-sm">
          <span className="font-medium">✨ {user.promo_code_used}</span> active - {trialDays} days remaining
        </div>
      )}
      
      {/* Trial Banner - Only for users WITHOUT a promo code */}
      {user && isSeller && onTrial && !hasPromoCode && !isFreeForLife && trialDays > 0 && (
        <div className="bg-gradient-to-r from-sky-500 to-teal-500 text-white text-center py-1.5 text-sm">
          <span className="font-medium">{trialDays} days left</span> in your seller trial.{' '}
          <Link to="/subscription" className="underline font-semibold hover:text-sky-100">
            Subscribe now
          </Link>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="/cm-logo-cropped.png" 
              alt="Crafters Market" 
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Search - Desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                type="text"
                placeholder="Search furniture, crafts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 bg-stone-50 border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                data-testid="search-input"
              />
            </div>
          </form>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-4">
            <Link to="/browse" className="text-stone-600 hover:text-teal-600 font-medium transition-colors" data-testid="browse-link">
              Browse
            </Link>
            <Link to="/workshops" className="text-stone-600 hover:text-teal-600 font-medium transition-colors flex items-center gap-1" data-testid="workshops-link">
              <GraduationCap className="h-4 w-4" />
              Workshops
            </Link>
            <Link to="/creator" className="text-stone-600 hover:text-teal-600 font-medium transition-colors flex items-center gap-1" data-testid="creator-link">
              <Video className="h-4 w-4" />
              Showroom
            </Link>
            <Link to="/trades" className="text-stone-600 hover:text-teal-600 font-medium transition-colors flex items-center gap-1" data-testid="trades-link">
              <Wrench className="h-4 w-4" />
              Trades
            </Link>
            <Link to="/about" className="text-stone-600 hover:text-teal-600 font-medium transition-colors" data-testid="about-link">
              About
            </Link>
            
            {user ? (
              <>
                {user.is_seller ? (
                  <Link to="/create-listing" data-testid="create-listing-link">
                    <Button className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium tracking-wide btn-active">
                      <Plus className="h-4 w-4 mr-2" />
                      Sell Item
                    </Button>
                  </Link>
                ) : (
                  <Link to="/subscription" data-testid="start-selling-link">
                    <Button className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium tracking-wide btn-active">
                      <Plus className="h-4 w-4 mr-2" />
                      Start Selling
                    </Button>
                  </Link>
                )}
                
                <NotificationBell />
                
                <Link to="/messages" className="relative p-2 text-stone-600 hover:text-teal-600 transition-colors" data-testid="messages-link">
                  <MessageSquare className="h-5 w-5" />
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 p-2 rounded-full hover:bg-stone-100 transition-colors" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                        <User className="h-4 w-4 text-teal-600" />
                      </div>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="font-semibold text-stone-900">{user.name}</p>
                      <p className="text-sm text-stone-500">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                      <User className="h-4 w-4 mr-2" />
                      Account Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="dashboard-menu-item">
                      <Package className="h-4 w-4 mr-2" />
                      My Listings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="messages-menu-item">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Messages
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/subscription')} data-testid="subscription-menu-item">
                      <User className="h-4 w-4 mr-2" />
                      Subscription
                    </DropdownMenuItem>
                    {user.is_seller && (
                      <DropdownMenuItem onClick={() => navigate('/seller/settings')} data-testid="seller-settings-menu-item">
                        <Settings className="h-4 w-4 mr-2" />
                        Seller Settings
                      </DropdownMenuItem>
                    )}
                    {(user.is_admin || user.email === 'ebonnie_faith@hotmail.com') && (
                      <>
                        <DropdownMenuSeparator />
                        <div className="px-3 py-1">
                          <p className="text-xs font-semibold text-stone-400 uppercase">Admin</p>
                        </div>
                        <DropdownMenuItem onClick={() => navigate('/admin/users')} data-testid="admin-users-menu-item">
                          <Users className="h-4 w-4 mr-2" />
                          Manage Users
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin/tradies')} data-testid="admin-tradies-menu-item">
                          <Wrench className="h-4 w-4 mr-2" />
                          Tradespeople
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin/support')} data-testid="admin-support-menu-item">
                          <HeadphonesIcon className="h-4 w-4 mr-2" />
                          Support Tickets
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin/broadcast')} data-testid="admin-broadcast-menu-item">
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Broadcast Message
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin/promotions')} data-testid="admin-promotions-menu-item">
                          <Star className="h-4 w-4 mr-2" />
                          Featured Carousel
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin/merchandise')} data-testid="admin-merchandise-menu-item">
                          <ShoppingBag className="h-4 w-4 mr-2" />
                          Merchandise
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-teal-600" data-testid="logout-menu-item">
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login" data-testid="login-link">
                  <Button variant="ghost" className="font-medium">Log In</Button>
                </Link>
                <Link to="/register" data-testid="register-link">
                  <Button className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium tracking-wide">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-stone-200">
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </form>
            
            <div className="space-y-2">
              <Link
                to="/browse"
                className="block px-3 py-2 rounded-md hover:bg-stone-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                Browse
              </Link>
              <Link
                to="/workshops"
                className="block px-3 py-2 rounded-md hover:bg-stone-100 flex items-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <GraduationCap className="h-4 w-4" />
                Workshops & Classes
              </Link>
              <Link
                to="/creator"
                className="block px-3 py-2 rounded-md hover:bg-stone-100 flex items-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Video className="h-4 w-4" />
                Crafters Showroom
              </Link>
              <Link
                to="/trades"
                className="block px-3 py-2 rounded-md hover:bg-stone-100 flex items-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Wrench className="h-4 w-4" />
                Find Trades
              </Link>
              <Link
                to="/about"
                className="block px-3 py-2 rounded-md hover:bg-stone-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                About Us
              </Link>
              
              {user ? (
                <>
                  <Link
                    to="/create-listing"
                    className="block px-3 py-2 rounded-md hover:bg-stone-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sell Item
                  </Link>
                  <Link
                    to="/dashboard"
                    className="block px-3 py-2 rounded-md hover:bg-stone-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Listings
                  </Link>
                  <Link
                    to="/creator/my-videos"
                    className="block px-3 py-2 rounded-md hover:bg-stone-100 flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Video className="h-4 w-4" />
                    My Showroom
                  </Link>
                  <Link
                    to="/messages"
                    className="block px-3 py-2 rounded-md hover:bg-stone-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Messages
                  </Link>
                  <Link
                    to="/subscription"
                    className="block px-3 py-2 rounded-md hover:bg-stone-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Subscription
                  </Link>
                  {user.is_seller && (
                    <Link
                      to="/seller/settings"
                      className="block px-3 py-2 rounded-md hover:bg-stone-100"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Seller Settings
                    </Link>
                  )}
                  {(user.is_admin || user.email === 'ebonnie_faith@hotmail.com') && (
                    <>
                      <div className="border-t border-stone-200 my-2 pt-2">
                        <p className="px-3 text-xs font-semibold text-stone-400 uppercase mb-2">Admin</p>
                        <Link
                          to="/admin/users"
                          className="block px-3 py-2 rounded-md hover:bg-stone-100"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Manage Users
                        </Link>
                        <Link
                          to="/admin/tradies"
                          className="block px-3 py-2 rounded-md hover:bg-stone-100"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Tradespeople
                        </Link>
                        <Link
                          to="/admin/support"
                          className="block px-3 py-2 rounded-md hover:bg-stone-100"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Support Tickets
                        </Link>
                        <Link
                          to="/admin/broadcast"
                          className="block px-3 py-2 rounded-md hover:bg-stone-100"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Broadcast Message
                        </Link>
                        <Link
                          to="/admin/promotions"
                          className="block px-3 py-2 rounded-md hover:bg-stone-100 flex items-center gap-2"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Star className="h-4 w-4 text-amber-500" />
                          Featured Carousel
                        </Link>
                        <Link
                          to="/admin/merchandise"
                          className="block px-3 py-2 rounded-md hover:bg-stone-100 flex items-center gap-2"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <ShoppingBag className="h-4 w-4 text-teal-600" />
                          Merchandise
                        </Link>
                      </div>
                    </>
                  )}
                  <button
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="block w-full text-left px-3 py-2 rounded-md hover:bg-stone-100 text-teal-600"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block px-3 py-2 rounded-md hover:bg-stone-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Log In
                  </Link>
                  <Link
                    to="/register"
                    className="block px-3 py-2 rounded-md hover:bg-stone-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
